import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { MessageCircle, Users, Filter, X, Star, Images, FileText, Download, CheckCircle2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { ChatWindow } from "../components/chat/ChatWindow";
import { Avatar } from "../components/chat/Avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hora = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return ""; } };
const dataHora = (iso) => { try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const Mensagens = () => {
  const { auth } = useApp();
  const token = auth?.token;
  const isAdmin = auth?.perfil === "administrador";
  const [conversas, setConversas] = useState([]);
  const [sel, setSel] = useState(null);
  const [de, setDe] = useState("");
  const [ate, setAte] = useState("");
  const [status, setStatus] = useState("");
  const [galeria, setGaleria] = useState(null); // { cpf, nome, itens }

  const fileUrl = (path) => `${API}/chat/arquivo?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(token || "")}`;

  const carregar = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (de) params.set("de", de);
      if (ate) params.set("ate", ate);
      if (status) params.set("status", status);
      const r = await axios.get(`${API}/chat/conversas?${params.toString()}`);
      setConversas(r.data || []);
    } catch (e) { /* ignore */ }
  }, [de, ate, status]);

  useEffect(() => { carregar(); const t = setInterval(carregar, 10000); return () => clearInterval(t); }, [carregar]);

  const limparFiltros = () => { setDe(""); setAte(""); setStatus(""); };

  const fixar = async (c, e) => {
    e.stopPropagation();
    try { await axios.post(`${API}/chat/conversas/${c.id}/fixar`); carregar(); } catch (err) { /* ignore */ }
  };

  const resolver = async (c, e) => {
    e.stopPropagation();
    try { await axios.post(`${API}/chat/conversas/${c.id}/resolver`); carregar(); } catch (err) { /* ignore */ }
  };

  const abrirGaleria = async (c) => {
    try {
      const r = await axios.get(`${API}/chat/anexos?familia_cpf=${encodeURIComponent(c.familiaCpf)}`);
      setGaleria({ cpf: c.familiaCpf, nome: c.familiaNome || c.familiaCpf, itens: r.data || [] });
    } catch (err) { setGaleria({ cpf: c.familiaCpf, nome: c.familiaNome, itens: [] }); }
  };

  return (
    <div className="space-y-4" data-testid="mensagens-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Mensagens</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          {isAdmin ? "Histórico de conversas entre a equipe e os familiares" : "Suas conversas com os familiares"}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3 flex items-end gap-3 flex-wrap" data-testid="mensagens-filtros">
        <div className="flex items-center gap-1.5 text-slate-500 text-sm font-medium"><Filter className="w-4 h-4" /> Filtrar</div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-0.5">De</label>
          <input type="date" value={de} onChange={(e) => setDe(e.target.value)} data-testid="filtro-de" className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm outline-none" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-0.5">Até</label>
          <input type="date" value={ate} onChange={(e) => setAte(e.target.value)} data-testid="filtro-ate" className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm outline-none" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-400 mb-0.5">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filtro-status" className="px-2.5 py-1.5 rounded-lg border border-slate-200 text-sm outline-none bg-white">
            <option value="">Todas</option>
            <option value="ativa">Ativas</option>
            <option value="encerrada">Encerradas</option>
          </select>
        </div>
        {(de || ate || status) && (
          <button onClick={limparFiltros} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="filtro-limpar"><X className="w-3 h-3" /> limpar</button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-20rem)] min-h-[420px]">
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Users className="w-4 h-4 text-blue-600" /> Conversas ({conversas.length})
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversas.length === 0 && <p className="text-sm text-slate-400 p-4 text-center">Nenhuma conversa encontrada.</p>}
            {conversas.map((c) => (
              <div key={c.id} onClick={() => setSel(c)} data-testid={`conversa-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${sel?.id === c.id ? "bg-blue-50" : ""} ${c.fixada ? "bg-amber-50/40" : ""} ${c.resolvida ? "opacity-60" : ""}`}>
                <div className="flex items-center gap-2.5">
                  <Avatar nome={c.nomeExibicao} foto={c.fotoExibicao} size={38} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {c.fixada && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400 shrink-0" />}
                        {c.resolvida && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                        <span className="font-medium text-slate-800 text-sm truncate">{c.nomeExibicao}</span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {c.naoLidas > 0 && <span className="text-[10px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5" data-testid={`conv-nao-${c.id}`}>{c.naoLidas}</span>}
                        <button onClick={(e) => resolver(c, e)} title={c.resolvida ? "Reabrir" : "Marcar resolvida"} data-testid={`conv-resolver-${c.id}`} className="p-0.5 rounded hover:bg-emerald-100">
                          <CheckCircle2 className={`w-4 h-4 ${c.resolvida ? "text-emerald-500" : "text-slate-300"}`} />
                        </button>
                        <button onClick={(e) => fixar(c, e)} title={c.fixada ? "Desafixar" : "Fixar no topo"} data-testid={`conv-fixar-${c.id}`} className="p-0.5 rounded hover:bg-amber-100">
                          <Star className={`w-4 h-4 ${c.fixada ? "text-amber-500 fill-amber-400" : "text-slate-300"}`} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{c.escopo === "interna" ? c.labelExibicao : (c.residentes.join(", ") || "—")}</p>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[11px] text-slate-400">{c.escopo === "interna" ? "conversa interna" : `com ${c.staffNome} · ${c.staffLabel}`}</span>
                      <span className="text-[10px] text-slate-300">{c.status === "encerrada" ? "encerrada" : hora(c.ultimoEm)}</span>
                    </div>
                    {isAdmin && !c.souParticipante && <span className="text-[9px] text-amber-600">histórico (leitura)</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden flex flex-col min-h-0">
          {sel ? (
            <>
              <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-end bg-slate-50">
                {sel.escopo !== "interna" && (
                  <button onClick={() => abrirGaleria(sel)} data-testid="ver-anexos" className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-lg">
                    <Images className="w-4 h-4" /> Anexos da família
                  </button>
                )}
              </div>
              <div className="flex-1 min-h-0">
                <ChatWindow key={sel.id} conversa={sel} meLado="equipe" readOnly={!sel.souParticipante} onBack={() => setSel(null)} onChanged={carregar} />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm flex-col gap-2">
              <MessageCircle className="w-8 h-8 text-slate-200" />
              Selecione uma conversa
            </div>
          )}
        </div>
      </div>

      {galeria && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setGaleria(null)} data-testid="galeria-anexos">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <div className="flex items-center gap-2 font-semibold text-slate-800"><Images className="w-4 h-4 text-blue-600" /> Anexos · {galeria.nome}</div>
              <button onClick={() => setGaleria(null)} className="p-1 rounded-md hover:bg-slate-100" data-testid="galeria-fechar"><X className="w-4 h-4 text-slate-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {galeria.itens.length === 0 ? <p className="text-sm text-slate-400 text-center py-10">Nenhum anexo trocado com esta família.</p> : (
                <>
                  {galeria.itens.some((i) => i.tipo === "imagem") && (
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-slate-500 mb-2">Fotos</p>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {galeria.itens.filter((i) => i.tipo === "imagem").map((i) => (
                          <a key={i.id} href={fileUrl(i.anexoPath)} target="_blank" rel="noreferrer" className="group relative" data-testid={`anexo-img-${i.id}`}>
                            <img src={fileUrl(i.anexoPath)} alt={i.anexoNome} className="w-full h-24 object-cover rounded-lg border border-slate-100" />
                            <span className="absolute bottom-0 inset-x-0 text-[9px] text-white bg-black/50 px-1 py-0.5 rounded-b-lg truncate">{dataHora(i.criadoEm)}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {galeria.itens.some((i) => i.tipo === "audio") && (
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-slate-500 mb-2">Áudios</p>
                      <div className="space-y-2">
                        {galeria.itens.filter((i) => i.tipo === "audio").map((i) => (
                          <div key={i.id} className="flex items-center gap-2 border border-slate-100 rounded-lg p-2" data-testid={`anexo-audio-${i.id}`}>
                            <audio controls src={fileUrl(i.anexoPath)} className="h-9 flex-1" />
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">{i.lado === "familia" ? "Família" : i.autorNome} · {dataHora(i.criadoEm)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {galeria.itens.some((i) => i.tipo === "arquivo") && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 mb-2">Documentos (PDF)</p>
                      <div className="space-y-1.5">
                        {galeria.itens.filter((i) => i.tipo === "arquivo").map((i) => (
                          <a key={i.id} href={fileUrl(i.anexoPath)} target="_blank" rel="noreferrer" className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid={`anexo-file-${i.id}`}>
                            <span className="flex items-center gap-2 text-sm text-slate-700 truncate"><FileText className="w-4 h-4 text-red-500 shrink-0" /> {i.anexoNome || "Documento"}</span>
                            <span className="flex items-center gap-2 text-[10px] text-slate-400 whitespace-nowrap">{dataHora(i.criadoEm)} <Download className="w-3.5 h-3.5" /></span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mensagens;
